import * as Sentry from '@sentry/vue';
import axios from 'axios';

import { awsAuth } from '@/services/aws-auth.service';

// Create axios instance (no cache interceptor due to CORS issues)
const apiClient = axios.create({
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Debug the axios configuration
console.log('Axios baseURL:', apiClient.defaults.baseURL);

// Add request interceptor for AWS authentication
// This MUST run for every request to attach the token
apiClient.interceptors.request.use(
  async config => {
    // Add Sentry breadcrumb for API request
    Sentry.addBreadcrumb({
      category: 'api',
      message: `API Request: ${config.method?.toUpperCase()} ${config.url}`,
      level: 'info',
      data: {
        method: config.method,
        url: config.url,
        params: config.params,
      },
    });

    try {
      // Try multiple sources for the token
      let idToken = await awsAuth.getIdToken();

      if (!idToken) {
        // Fallback to localStorage
        idToken = localStorage.getItem('idToken');
      }

      if (!idToken) {
        // Fallback to cookie
        const cookieToken = document.cookie
          .split('; ')
          .find(row => row.startsWith('idToken='))
          ?.split('=')[1];

        if (cookieToken && cookieToken !== 'null') {
          idToken = cookieToken;
        }
      }

      if (idToken && idToken !== 'null' && idToken !== 'undefined') {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${idToken}`;
      } else {
        console.warn('⚠️ No token available for request');
        Sentry.addBreadcrumb({
          category: 'auth',
          message: 'No authentication token available for API request',
          level: 'warning',
        });
      }
    } catch (error) {
      console.error('❌ Error in interceptor:', error);
      Sentry.captureException(error, {
        tags: { context: 'api_interceptor' },
      });
    }

    return config;
  },
  error => {
    console.error('❌ Request interceptor error:', error);
    Sentry.captureException(error, {
      tags: { context: 'api_request_interceptor' },
    });
    return Promise.reject(error);
  },
);

// Handle Lambda response format
apiClient.interceptors.response.use(
  response => {
    // Add Sentry breadcrumb for successful API response
    Sentry.addBreadcrumb({
      category: 'api',
      message: `API Response: ${response.status} ${response.config.url}`,
      level: 'info',
      data: {
        status: response.status,
        url: response.config.url,
      },
    });

    // Lambda returns data in response.data directly for HTTP API
    return response;
  },
  async error => {
    // Add Sentry breadcrumb for API error
    Sentry.addBreadcrumb({
      category: 'api',
      message: `API Error: ${error.response?.status || 'Network Error'} ${error.config?.url}`,
      level: 'error',
      data: {
        status: error.response?.status,
        url: error.config?.url,
        message: error.message,
      },
    });

    // Handle token expiration
    if (error.response?.status === 401) {
      try {
        await awsAuth.refreshToken();
        // Retry the request with new token
        const idToken = await awsAuth.getIdToken();
        error.config.headers.Authorization = `Bearer ${idToken}`;
        return apiClient.request(error.config);
      } catch (refreshError) {
        // Capture refresh token failure
        Sentry.captureException(refreshError, {
          tags: { context: 'token_refresh_failed' },
        });
        // Redirect to login if refresh fails
        window.location.href = '/auth/sign-in';
      }
    }

    // Capture non-401 errors to Sentry
    if (error.response?.status !== 401) {
      Sentry.captureException(error, {
        tags: {
          context: 'api_error',
          status_code: error.response?.status,
        },
        extra: {
          url: error.config?.url,
          method: error.config?.method,
          response: error.response?.data,
        },
      });
    }

    return Promise.reject(error);
  },
);

/**
 * Generic API request function
 */
export async function apiRequest(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  url: string,
  data?: any,
  params?: Record<string, string>,
) {
  const response = await apiClient.request({
    method,
    url,
    data,
    params,
  });
  return response.data;
}

// Export both as named and default for compatibility
export const api = apiClient;
